import axios, { AxiosRequestConfig } from 'axios'
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import CheckIcon from '@mui/icons-material/Check'
import SaveIcon from '@mui/icons-material/Save'
import UndoIcon from '@mui/icons-material/Undo'
import InfoIcon from '@mui/icons-material/Info'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ITimecard } from '../interface/ISelectedDate'
import { useMsal } from '@azure/msal-react'
import { ICustomSnackbar } from '../interface/ICustomSnackbar'
import { setAlert } from '../source/slice/AlertSlice'
import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import { UpdateStoreHelper } from '../source/StoreHelper'
import { ErrorStatusCode } from '../interface/ErrorStatusCode'
import { IJournal } from '../interface/IJournal'
import { IUser } from '../interface/IUser'
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { setLoadingDate, setRequireCalendarReload } from '../source/slice/LoadingDateSlice'
import LaunchIcon from '@mui/icons-material/Launch'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import SickIcon from '@mui/icons-material/Sick'
import { getAuthConfig } from '../utils/authApi'
import { EmptyGuid } from '../utils/values'
import { UpdateStoreHelperJournal } from '@/source/StoreHelperJournal'
import { useUnsavedChanges } from './Warnings/ChangedDataWarning'
import { ICalendar } from '@/interface/ICalendar'

const Footer = () => {
    const { t } = useTranslation()
    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const selectedJournal: IJournal = useSelector((state: any) => state.journal?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const calendarDates: ICalendar[] = useSelector((state: any) => state.calendarDates?.value)
    const absenceBookedForSelectedJournalDay: boolean = useSelector((state: any) => state.loadingDate?.journalAbsenceBooked)
    const { instance, accounts } = useMsal()
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [openDialog, setOpenDialog] = useState(false)
    const popoverRef = React.useRef<HTMLDivElement | null>(null)

    const isInternal: boolean = !!(user?.worker?.mserp_employmentcategory && (user?.worker?.mserp_employmentcategory === process.env.REACT_APP_USER_INTERNAL || user?.worker?.mserp_employmentcategory === process.env.REACT_APP_USER_STUDENT))
    const isStudent: boolean = !!user?.isWorkingStudent
    const isANU: boolean = !!(user?.worker?.mserp_employmentcategory && user?.worker?.mserp_employmentcategory === 'ANÜ')
    const calendarBlocked: boolean = !!((selectedDate?.header?.mserp_timeprofileid && selectedDate?.header?.mserp_timeprofileid === 'BLOCK') || (selectedDate?.header?.specialDayId && selectedDate?.header?.specialDayId === 'REDUCE'))
    const hasExistingTimecardHeaderId: boolean = !!selectedDate?.header?.mserp_mbelectronictimecardheaderentityid && selectedDate.header.mserp_mbelectronictimecardheaderentityid !== EmptyGuid

    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)

    const currentCalendarDate = calendarDates?.find((date) => {
        const journalDate = selectedJournal?.header?.date
        if (!journalDate) return false
        const today = new Date(journalDate)
        const calendarDate = new Date(date.date)
        return calendarDate.getFullYear() === today.getFullYear() && calendarDate.getMonth() === today.getMonth() && calendarDate.getDate() === today.getDate()
    })
    const isAbsenceDay = currentCalendarDate?.status === 'ABSENCE' || absenceBookedForSelectedJournalDay

    const [backendModalOpen, setBackendModalOpen] = useState(false)
    const [backendModalMessage, setBackendModalMessage] = useState('')
    const [workTimeViolation, setWorkTimeViolation] = useState(false)

    const [loadingSave, setLoadingSave] = useState(false)
    const [loadingRelease, setLoadingRelease] = useState(false)

    type ErrorCode = 101 | 102 | 103 | 9000 | 9001 | 9002 | 9003 | 9004 | 9005 | 9101 | 9102 | 9104 | 9105 | 9106 | 9107 | 9108 | 9201 | 9202 | 9301 | 9302 | 9401 | 9402 | 9403 | 9404 | 9501 | 9502

    const customSnackbarMessageSuccess: ICustomSnackbar = {
        message: t('zdeheader.saveSuccess'),
        type: 'success',
    }
    const customSnackbarMessageError: ICustomSnackbar = {
        message: t('zdeheader.saveError'),
        type: 'error',
    }

    const customSnackbarMessageReleaseSuccess: ICustomSnackbar = {
        message: t('zdeheader.releaseSuccess'),
        type: 'success',
    }
    const customSnackbarMessageReleaseError: ICustomSnackbar = {
        message: t('zdeheader.releaseError'),
        type: 'error',
    }

    const customSnackbarMessageReopenSuccess: ICustomSnackbar = {
        message: t('zdeheader.reopenSuccess'),
        type: 'success',
    }
    const customSnackbarMessageReopenError: ICustomSnackbar = {
        message: t('zdeheader.reopenError'),
        type: 'error',
    }

    const { resetBaseline } = useUnsavedChanges()

    const handleClick = () => {
        if (user?.worker?.mserp_usetimecard) {
            postTimeCard()
        } else {
            postJournal()
        }
    }

    const handleReleaseClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const simulateBackendResponse = (code: ErrorCode, message?: string, codes?: ErrorCode[]) => {
        const errorMessages: Record<number, string> = {
            9000: t('footer.backendErrors.maximumtime') + ' ' + t('footer.backendErrors.missingComment'),
            9001: t('footer.backendErrors.breaktime') + ' ' + t('footer.backendErrors.missingComment'),
            9002: t('footer.backendErrors.dayafter') + ' ' + t('footer.backendErrors.missingComment'),
            9003: t('footer.backendErrors.daybefore') + ' ' + t('footer.backendErrors.missingComment'),
            9004: t('footer.backendErrors.unknownError'),
            9005: t('footer.backendErrors.sundaywork') + ' ' + t('footer.backendErrors.missingComment'),
            9101: t('footer.backendErrors.toomuchdistributed'),
            9102: t('footer.backendErrors.toolessdistributed'),
            9104: t('footer.backendErrors.missingactivity'),
            9105: t('footer.backendErrors.missingorder'),
            9108: t('footer.backendErrors.orderquantityexceeded'),
            9106: t('footer.backendErrors.missingfromtoheader'),
            9107: t('footer.backendErrors.negativeentry'),
            9201: t('footer.backendErrors.nocapacity'),
            9202: t('footer.backendErrors.hoursalreadydistributed'),
            9301: t('footer.backendErrors.undefinedreopenerror'),
            9302: t('footer.backendErrors.notpermittedperiod'),
            9401: t('footer.backendErrors.timecardalreadyexists'),
            9402: t('footer.backendErrors.journalalreadyexists'),
            9403: t('footer.backendErrors.journalinvalidhours'),
            9404: t('footer.backendErrors.maximumtimejournal'),
            9501: t('footer.backendErrors.dateoutsideentryperiod'),
            9502: t('footer.backendErrors.releasefuturedays'),
            101: t('footer.backendErrors.missingFields'),
            102: t('footer.backendErrors.missingComment'),
            103: t('footer.backendErrors.unknownError'),
            //TODO:  to be extended (more codes)
        }

        const isWorkTimeViolationCode = (violationCode: number) => {
            return violationCode === 9000 || violationCode === 9001 || violationCode === 9002 || violationCode === 9003 || violationCode === 9005
        }

        const violationItemText = (violationCode: number) => {
            switch (violationCode) {
                case 9000:
                    return t('footer.backendErrors.violationItems.maximumtime') + ': ' + t('footer.backendErrors.maximumtime')
                case 9001:
                    return t('footer.backendErrors.violationItems.breaktime') + ': ' + t('footer.backendErrors.breaktime')
                case 9002:
                    return t('footer.backendErrors.violationItems.dayafter') + ': ' + t('footer.backendErrors.dayafter')
                case 9003:
                    return t('footer.backendErrors.violationItems.daybefore') + ': ' + t('footer.backendErrors.daybefore')
                case 9005:
                    return t('footer.backendErrors.violationItems.sundaywork') + ': ' + t('footer.backendErrors.sundaywork')
                default:
                    return t('footer.backendErrors.unknownError')
            }
        }

        const simulateError = true

        if (simulateError) {
            const allCodes: number[] = Array.isArray(codes) && codes.length > 0 ? codes : [code]
            const workTimeViolationCodes = allCodes.filter(isWorkTimeViolationCode)

            if (workTimeViolationCodes.length > 1) {
                setWorkTimeViolation(true)
                const violationList = `\n• ${workTimeViolationCodes.map(violationItemText).join('\n• ')}`
                setBackendModalMessage(
                    t('footer.backendErrors.multipleviolations', {
                        violations: violationList,
                        comment: t('footer.backendErrors.missingComment'),
                    })
                )
            } else {
                let errorMessage = errorMessages[code] || t('footer.backendErrors.unknownError')
                if (message) {
                    errorMessage = errorMessage.replace('{{message}}', message)
                }
                if (isWorkTimeViolationCode(code)) {
                    setWorkTimeViolation(true)
                }
                setBackendModalMessage(`${errorMessage}`)
            }
        } else {
            setBackendModalMessage(t('footer.backendResponse'))
        }
        setBackendModalOpen(true)
    }

    const parseViolationCodes = (err?: ErrorStatusCode): ErrorCode[] => {
        if (!err) return []
        if (Array.isArray(err.VIOLATION_CODES) && err.VIOLATION_CODES.length > 0) {
            return err.VIOLATION_CODES.map((c) => Number(c)).filter((c) => Number.isFinite(c)) as ErrorCode[]
        }
        if (typeof err.VIOLATION_CODE === 'number') {
            return [Number(err.VIOLATION_CODE) as ErrorCode]
        }
        return []
    }

    const handleReleaseWithIllness = () => {
        //TODO: to be extended (backend API call)
        setOpenDialog(true)
        setAnchorEl(null)
    }

    const handleReleaseAsNotWorked = () => {
        setAnchorEl(null)
        releaseTimeCardAsNotWorked()
    }

    const handleDialogClose = () => {
        setOpenDialog(false)
    }

    const handleDialogConfirm = () => {
        releaseTimeCardWithIllness()
        setOpenDialog(false)
    }

    const scope: string = process.env.REACT_APP_SCOPE || ''

    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    const postTimeCard = () => {
        setLoadingSave(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        /*code: process.env.REACT_APP_BACKEND_URL*/
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/Timecard`, JSON.stringify(selectedDate), config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                                resetBaseline()
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch((error) => {
                        dispatch(setLoadingDate(false))
                        dispatch(setAlert(customSnackbarMessageError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                    })
                    .finally(() => {
                        setLoadingSave(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const postJournal = () => {
        setLoadingSave(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        /*code: process.env.REACT_APP_BACKEND_URL*/
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/Journal`, JSON.stringify(selectedJournal), config)
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedJournal.header?.date) {
                                await UpdateStoreHelperJournal(selectedJournal.header?.date, true)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageError))
                            dispatch(setLoadingDate(false))
                        }
                        console.log(response)
                    })
                    .catch((error) => {
                        dispatch(setLoadingDate(false))
                        dispatch(setAlert(customSnackbarMessageError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                    })
                    .finally(() => {
                        setLoadingSave(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const updateDate = (date: string) => {
        UpdateStoreHelper(format(date, 'yyyy-MM-dd'), true)
    }

    const releaseTimeCard = () => {
        setLoadingRelease(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedDate?.header?.mserp_timeprofiledate ? format(selectedDate.header.mserp_timeprofiledate, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReleaseTimecard`, JSON.stringify(selectedDate), config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReleaseSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReleaseError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReleaseError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
        resetBaseline()
    }

    const reopenTimeCard = () => {
        setLoadingRelease(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedDate?.header?.mserp_timeprofiledate ? format(selectedDate.header.mserp_timeprofiledate, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReopenTimecard`, null, config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReopenSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReopenError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReopenError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const reopenJournal = () => {
        setLoadingRelease(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedJournal?.header?.date ? format(selectedJournal?.header?.date, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReopenJournal`, null, config)
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReopenSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedJournal?.header?.date) {
                                await UpdateStoreHelperJournal(selectedJournal?.header?.date, true)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReopenError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReopenError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const releaseTimeCardWithIllness = () => {
        setLoadingRelease(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedDate?.header?.mserp_timeprofiledate ? format(selectedDate.header.mserp_timeprofiledate, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReleaseTimecardWithIllness`, JSON.stringify(selectedDate), config)
                    .then((response) => {
                        console.log('RESPONSE', response)
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReleaseSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReleaseError))
                            dispatch(setLoadingDate(false))
                        }
                        console.log(response)
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReleaseError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
        resetBaseline()
    }

    const releaseTimeCardAsNotWorked = () => {
        setLoadingRelease(true)

        const emptyTimecard: ITimecard = {
            ...selectedDate,
            lines: [],
            oncallRequests: [],
        }

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedDate?.header?.mserp_timeprofiledate ? format(selectedDate.header.mserp_timeprofiledate, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReleaseTimecard`, JSON.stringify(emptyTimecard), config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReleaseSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header?.mserp_timeprofiledate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReleaseError))
                            dispatch(setLoadingDate(false))
                        }
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReleaseError))
                        const err: ErrorStatusCode = error.response?.data
                        if (err?.VIOLATION_CODE) {
                            const errorCode: ErrorCode = err?.VIOLATION_CODE as ErrorCode
                            simulateBackendResponse(errorCode)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })

        resetBaseline()
    }

    const releaseJournal = () => {
        setLoadingRelease(true)
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: selectedJournal.header?.date ? format(selectedJournal.header?.date, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .post(`${process.env.REACT_APP_BACKEND_URL}/ReleaseJournal`, JSON.stringify(selectedJournal), config)
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageReleaseSuccess))
                            dispatch(setRequireCalendarReload(true))
                            if (selectedJournal.header?.date) {
                                await UpdateStoreHelperJournal(selectedJournal.header?.date, true)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                        } else {
                            dispatch(setAlert(customSnackbarMessageReleaseError))
                            dispatch(setLoadingDate(false))
                        }
                        console.log(response)
                    })
                    .catch((error) => {
                        dispatch(setAlert(customSnackbarMessageReleaseError))
                        const err: ErrorStatusCode = error.response?.data
                        const violationCodes = parseViolationCodes(err)
                        if (violationCodes.length > 0) {
                            const primaryCode: ErrorCode = (err?.VIOLATION_CODE ?? violationCodes[0]) as ErrorCode
                            simulateBackendResponse(primaryCode, err?.MESSAGE, violationCodes)
                        }
                        dispatch(setLoadingDate(false))
                    })
                    .finally(() => {
                        setLoadingRelease(false)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const handleKeyPress = (event: KeyboardEvent) => {
        if (event.altKey) {
            switch (event.key) {
                case 's':
                    if (selectedDate?.header?.isTransferred || selectedDate?.dayInformation?.mserp_transferred || calendarBlocked || selectedJournal?.header?.mserp_posted || loadingDate || isAbsenceDay) break
                    handleClick()
                    break
                case 'f':
                    if (selectedDate?.header?.isTransferred || selectedDate?.dayInformation?.mserp_transferred || calendarBlocked || selectedJournal?.header?.mserp_posted || loadingDate || isAbsenceDay) break
                    if (user?.worker?.mserp_usetimecard) {
                        releaseTimeCard()
                    } else {
                        releaseJournal()
                    }
                    break
                case 'r':
                    if (user?.worker?.mserp_usetimecard) {
                        if ((!selectedDate?.header?.isTransferred && !selectedDate?.header?.calculated && !selectedDate?.dayInformation?.mserp_transferred) || calendarBlocked || loadingDate || isAbsenceDay) break
                        reopenTimeCard()
                    } else {
                        if (!selectedJournal.header?.mserp_posted || calendarBlocked || loadingDate || isAbsenceDay) break
                        reopenJournal()
                    }
                    break
                default:
            }
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)
        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [selectedDate, selectedJournal, user, calendarBlocked, loadingDate])

    const TextCenterTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)({
        [`& .${tooltipClasses.tooltip}`]: {
            textAlign: 'center',
            lineHeight: '1.5em',
            padding: '8px 12px',
        },
    })

    return (
        <Paper
            sx={{
                position: 'fixed',
                bottom: 0,
                width: '100vw',
            }}
        >
            <Paper sx={{ bottom: 0 }}>
                <Box
                    sx={{
                        p: 1,
                        py: { xs: '12px', md: 1 },
                        display: 'flex',
                        gap: 1,
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        alignItems: 'center',
                        flexDirection: { xs: 'column-reverse', md: 'row' },
                    }}
                >
                    {(isInternal || isANU) &&
                        (selectedDate.totalHours !== undefined ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: { xs: '4px', sm: '10px' },
                                    marginRight: { xs: '0', md: '12px' },
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    '@media (max-width: 359px)': { fontSize: '0.8rem' },
                                }}
                            >
                                <TextCenterTooltip
                                    title={
                                        <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Gesamtsumme.aspx" color="#fff" target="_blank" rel="noopener">
                                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: '9pt' }}>{t('footer.totalamountdescription')} </Typography>
                                                <LaunchIcon style={{ fontSize: 15 }} />
                                            </Stack>
                                        </Link>
                                    }
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: 'fit-content' }}>
                                        {t('footer.totalamount', {
                                            date: selectedDate.totalHours?.date ? format(new Date(selectedDate.totalHours.date), 'dd.MM.yyyy') : '-',
                                            amount: selectedDate.totalHours.hours,
                                            formatParams: {
                                                amount: {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                },
                                            },
                                        })}
                                    </Box>
                                </TextCenterTooltip>
                                {isInternal && (
                                    <Box sx={{ display: 'flex' }}>
                                        {!isStudent && (
                                            <>
                                                {selectedDate.totalHours.hours < -60 && (
                                                    <TextCenterTooltip title={t('footer.redLightMinus')}>
                                                        <ErrorIcon
                                                            color="error"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours >= -60 && selectedDate.totalHours.hours < -40 && (
                                                    <TextCenterTooltip title={t('footer.yellowLightMinus')}>
                                                        <WarningIcon
                                                            color="warning"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours >= -40 && selectedDate.totalHours.hours <= 120 && (
                                                    <TextCenterTooltip title={t('footer.greenLightInfo')}>
                                                        <CheckCircleIcon
                                                            color="success"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours > 120 && selectedDate.totalHours.hours <= 160 && (
                                                    <TextCenterTooltip
                                                        title={t('footer.yellowLightPlus')}
                                                        sx={{
                                                            textWrap: 'balance',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <WarningIcon
                                                            color="warning"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours > 160 && (
                                                    <TextCenterTooltip title={t('footer.redLightPlus')}>
                                                        <ErrorIcon
                                                            color="error"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                            </>
                                        )}
                                        {isStudent && (
                                            <>
                                                {selectedDate.totalHours.hours < -20 && (
                                                    <TextCenterTooltip title={t('footer.redLightMinus')}>
                                                        <ErrorIcon
                                                            color="error"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours >= -20 && selectedDate.totalHours.hours <= 40 && (
                                                    <TextCenterTooltip title={t('footer.greenLightInfo')}>
                                                        <CheckCircleIcon
                                                            color="success"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                                {selectedDate.totalHours.hours > 40 && (
                                                    <TextCenterTooltip title={t('footer.redLightPlus')}>
                                                        <ErrorIcon
                                                            color="error"
                                                            sx={{
                                                                width: { xs: '0.9em', sm: '1em' },
                                                                height: { xs: '0.9em', sm: '1em' },
                                                            }}
                                                        />
                                                    </TextCenterTooltip>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Skeleton variant="text" sx={{ maxWidth: 'calc(100% - 32px)', width: 300 }} />
                        ))}
                    {!selectedDate?.header?.isTransferred && !selectedJournal?.header?.mserp_posted ? (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button
                                variant="outlined"
                                size="medium"
                                loading={loadingSave}
                                loadingPosition="end"
                                onClick={handleClick}
                                sx={{ ml: { xs: 0, md: 1 }, mr: { xs: '6px', sm: 1 } }}
                                startIcon={<SaveIcon sx={{ '@media (max-width: 359px)': { display: 'none' } }} />}
                                disabled={selectedDate?.header?.isTransferred || calendarBlocked || selectedJournal?.header?.mserp_posted || loadingDate || isAbsenceDay}
                            >
                                {t('footer.save')}
                                <TextCenterTooltip title={t('footer.saveinfo')} placement="top">
                                    <InfoIcon color="disabled" fontSize="small" sx={{ ml: 1 }} />
                                </TextCenterTooltip>
                            </Button>
                            <div ref={popoverRef} style={{ position: 'absolute', top: 0, left: 0 }}></div>
                            <ButtonGroup variant="contained" sx={{ marginRight: { xs: '0', md: '24px' } }}>
                                <Button
                                    size="medium"
                                    loading={loadingRelease}
                                    loadingPosition="end"
                                    onClick={user?.worker?.mserp_usetimecard ? releaseTimeCard : releaseJournal}
                                    startIcon={<CheckIcon sx={{ '@media (max-width: 359px)': { display: 'none' } }} />}
                                    disabled={selectedDate?.header?.isTransferred || calendarBlocked || selectedJournal?.header?.mserp_posted || loadingDate || isAbsenceDay}
                                >
                                    {t('footer.release')}
                                </Button>

                                {isInternal && (
                                    <Button size="small" onClick={handleReleaseClick} disabled={selectedDate?.header?.isTransferred || calendarBlocked || loadingDate || isAbsenceDay}>
                                        <ArrowDropDownIcon />
                                    </Button>
                                )}
                            </ButtonGroup>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            {user?.worker?.mserp_usetimecard ? (
                                <Button
                                    variant="contained"
                                    size="medium"
                                    loading={loadingRelease}
                                    loadingPosition="end"
                                    onClick={reopenTimeCard}
                                    startIcon={<UndoIcon sx={{ '@media (max-width: 359px)': { display: 'none' } }} />}
                                    disabled={!selectedDate?.header?.isTransferred || calendarBlocked || loadingDate || isAbsenceDay}
                                >
                                    {t('footer.reopen')}
                                </Button>
                            ) : (
                                <Button
                                    sx={{ marginRight: { xs: '0', md: '24px' } }}
                                    variant="contained"
                                    size="medium"
                                    color="warning"
                                    loading={loadingRelease}
                                    loadingPosition="end"
                                    onClick={reopenJournal}
                                    startIcon={<UndoIcon sx={{ '@media (max-width: 359px)': { display: 'none' } }} />}
                                    disabled={!selectedJournal.header?.mserp_posted || calendarBlocked || loadingDate || isAbsenceDay}
                                >
                                    {t('footer.reopen')}
                                </Button>
                            )}
                        </Box>
                    )}
                    {isInternal && (
                        <Menu id="release-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                            {isStudent && (
                                <Tooltip title={hasExistingTimecardHeaderId ? t('footer.releaseAsNotWorkedDisabledTooltip') : ''} placement="left">
                                    <span>
                                        <MenuItem onClick={handleReleaseAsNotWorked} disabled={selectedDate?.header?.isTransferred || calendarBlocked || loadingDate || hasExistingTimecardHeaderId}>
                                            <ListItemIcon>
                                                <EventBusyIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>{t('footer.releaseAsNotWorked')}</ListItemText>
                                        </MenuItem>
                                    </span>
                                </Tooltip>
                            )}
                            <MenuItem onClick={handleReleaseWithIllness}>
                                <ListItemIcon>
                                    <SickIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t('footer.releaseWithIllness')}</ListItemText>
                            </MenuItem>
                        </Menu>
                    )}
                </Box>
            </Paper>

            <Dialog open={openDialog} onClose={() => handleDialogClose()}>
                <DialogTitle>{t('footer.releaseWithIllness')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('footer.releaseWithIllnessConfirmation')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogClose()} color="primary">
                        {t('footer.cancel')}
                    </Button>
                    <Button onClick={() => handleDialogConfirm()} color="primary">
                        {t('footer.release')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={backendModalOpen}
                onClose={() => {
                    setBackendModalOpen(false)
                    if (workTimeViolation && selectedDate.header?.mserp_timeprofiledate) {
                        updateDate(selectedDate.header.mserp_timeprofiledate)
                        setWorkTimeViolation(false)
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ErrorOutlineIcon color="error" sx={{ marginRight: '8px' }} />
                        <Typography variant="h6" component="div">
                            {t('footer.error')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{backendModalMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setBackendModalOpen(false)
                            if (workTimeViolation && selectedDate.header?.mserp_timeprofiledate) {
                                updateDate(selectedDate.header.mserp_timeprofiledate)
                                setWorkTimeViolation(false)
                            }
                        }}
                        color="primary"
                    >
                        {t('footer.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default Footer
